/**
 * Hook para realtime seguindo documentação oficial do Supabase
 * Implementação simples e direta
 */

import { useEffect, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';

export const useRealtimeStudents = (studentIds, onStudentUpdate) => {
  const [isConnected, setIsConnected] = useState(false);
  const [status, setStatus] = useState('CLOSED');
  const channelRef = useRef(null);

  useEffect(() => {
    // Se não temos estudantes, não conecta
    if (!studentIds || !Array.isArray(studentIds) || studentIds.length === 0) {
      console.log('🔴 useRealtimeStudents: Nenhum estudante para monitorar', { studentIds });
      setIsConnected(false);
      setStatus('CLOSED');
      return;
    }

    // Se não temos callback, não conecta
    if (!onStudentUpdate || typeof onStudentUpdate !== 'function') {
      console.log('🔴 useRealtimeStudents: Nenhum callback fornecido');
      setIsConnected(false);
      setStatus('CLOSED');
      return;
    }

    console.log('🟢 useRealtimeStudents: Configurando realtime para estudantes:', studentIds);

    // Criar canal simples seguindo documentação
    const channel = supabase
      .channel('students-changes', {
        config: {
          postgres_changes: [
            {
              event: 'UPDATE',
              schema: 'public',
              table: 'students',
              filter: `id=in.(${studentIds.join(',')})`
            }
          ]
        }
      })
      .on(
        'postgres_changes',
        { 
          event: 'UPDATE', 
          schema: 'public', 
          table: 'students' 
        },
        (payload) => {
          console.log('📨 Evento recebido:', payload);
          
          // Verificar se é um dos nossos estudantes
          if (payload.new && studentIds.includes(payload.new.id)) {
            console.log('✅ Processando atualização do estudante:', payload.new.id);
            
            // Verificar se houve mudança no saldo (possível recarga PIX)
            const oldBalance = payload.old?.balance || 0;
            const newBalance = payload.new?.balance || 0;
            
            if (newBalance > oldBalance) {
              const difference = newBalance - oldBalance;
              console.log(`💰 Realtime: Saldo aumentou! +R$ ${difference.toFixed(2)} para ${payload.new.name}`);
              
              // Disparar evento customizado para o portal detectar recarga
              window.dispatchEvent(new CustomEvent('pixPaymentReceived', {
                detail: {
                  studentId: payload.new.id,
                  studentName: payload.new.name,
                  oldBalance,
                  newBalance,
                  amount: difference
                }
              }));
            }
            
            onStudentUpdate(payload.new);
          } else {
            console.log('❌ Estudante não é nosso:', payload.new?.id);
          }
        }
      )
      .subscribe((status) => {
        console.log('📡 Status do canal:', status);
        setStatus(status);
        setIsConnected(status === 'SUBSCRIBED');
        
        if (status === 'SUBSCRIBED') {
          console.log('✅ Realtime conectado com sucesso!');
        } else if (status === 'CHANNEL_ERROR') {
          console.error('❌ Erro no canal realtime');
        } else if (status === 'TIMED_OUT') {
          console.warn('⏰ Timeout na conexão realtime');
        } else if (status === 'CLOSED') {
          console.log('🔒 Canal realtime fechado');
        }
      });

    channelRef.current = channel;

    // Cleanup
    return () => {
      console.log('🧹 Limpando canal realtime');
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      setIsConnected(false);
      setStatus('CLOSED');
    };

  }, [studentIds ? studentIds.join(',') : '']); // Reagir apenas quando IDs mudarem

  // Função para reconectar manualmente
  const reconnect = () => {
    console.log('🔄 Reconectando realtime...');
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    // O useEffect vai recriar a conexão automaticamente
  };

  return {
    isConnected,
    status,
    reconnect
  };
};